import { Box, IconButton, InputBase } from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import { useState, type KeyboardEvent } from 'react'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { KNOWLEDGE_QUESTION_MAX_CHARS } from '@/features/internal/knowledge'
import { ICON_SIZE, scaledFontSize } from '@/shared/styles/typography'
import { RADIUS } from '@/shared/styles/radii'

interface Props {
  disabled: boolean
  placeholder: string
  onSubmit: (question: string) => void
}

/**
 * 質問入力欄。枠線を持たず背景色だけで affordance を伝える (text-field.md の inline 系)。
 *
 * 送信は Enter。改行は Shift+Enter。制度の照会は状況を数行書くことがあるため
 * 複数行を許し、行数に応じて伸びる。
 */
export const QuestionInput = ({ disabled, placeholder, onSubmit }: Props) => {
  const [value, setValue] = useState('')

  const submit = () => {
    const question = value.trim()
    if (!question || disabled) return
    setValue('')
    onSubmit(question)
  }

  const handleKeyDown = (e: KeyboardEvent) => {
    // IME 変換中の Enter (変換確定) では送信しない。**この欄は入力の全てが日本語の
    // 照会文**なので、塞がないと変換を確定した時点で未完成の質問が飛ぶ。submit は
    // onSubmit より先に入力を空にするため打ち直しも効かず、back は切れた質問を記録して
    // 生成も 1 回走る。KeywordInput で同じ欠陥を直した先例がある (log/179 work-log/8)。
    if (e.nativeEvent.isComposing) return
    if (e.key !== 'Enter' || e.shiftKey) return
    e.preventDefault()
    submit()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        px: 1.5,
        py: 1.25,
        borderRadius: `${RADIUS.md}px`,
        backgroundColor: colorTokens.bg.subtle,
        transition: 'background-color 120ms',
        '&:focus-within': { backgroundColor: colorTokens.bg.muted },
      }}
    >
      <InputBase
        multiline
        maxRows={8}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        sx={{
          flex: 1,
          // 表示と入力で字格を揃える (text-field.md)。文字サイズモードにも追従させる。
          fontSize: scaledFontSize('bodySm'),
          lineHeight: 1.6,
          color: colorTokens.fg.default,
        }}
        // 字数は back が拒む値と同じところで止める。カウンタは出さない — 通常の照会は
        // 上限の 1/10 にも届かず、常に出る表示は上限が近いという誤った合図になる。
        inputProps={{
          'aria-label': placeholder,
          maxLength: KNOWLEDGE_QUESTION_MAX_CHARS,
        }}
      />
      <IconButton
        size="small"
        disabled={disabled || !value.trim()}
        onClick={submit}
        aria-label="質問する"
        sx={{ p: 0.5 }}
      >
        {/* icons.md: MUI の fontSize="small" (≈20px) は使わず ICON_SIZE に揃える */}
        <ArrowUpwardIcon sx={{ fontSize: ICON_SIZE.md }} />
      </IconButton>
    </Box>
  )
}
